/**
 * DsDataGrid — the KEYSTONE shared data grid for the Fox & Fable Data section.
 *
 * Renders the exact ds-tbl (Stripe/Shopify) visual signature — uppercase
 * 10.5px headers, 32px dense rows, ink-75 dividers, hover tint — but windows
 * the body with react-window v2 so 10k+ rows scroll without DOM bloat.
 *
 * Because react-window positions each row with an absolute transform, the body
 * cannot be a real <table>. Instead the header and every body row are CSS grids
 * that share one column template (built from each column's `width`), so the
 * windowed rows line up perfectly under the sticky header. The ds-tbl classes
 * (.ds-tbl, .ds-r, .ds-c, .ds-id, hover tint, dividers) are reused verbatim so
 * the grid is visually indistinguishable from the table primitive.
 *
 * Below VIRTUAL_THRESHOLD rows we skip windowing entirely (cheaper, and avoids
 * a scroll container for short tables); above it we mount react-window's List.
 */
import React, { useMemo, useState } from 'react';
import { List, type RowComponentProps } from 'react-window';
import { EmptyState } from './EmptyState';

export type DsGridAlign = 'left' | 'right' | 'center';
export type DsSortDir = 'asc' | 'desc';

export interface DsColumn<Row> {
  /** Stable identity for the column; also used as React key. */
  key: string;
  /** Header label (uppercased by CSS). */
  header: React.ReactNode;
  align?: DsGridAlign;
  /** Render the cell as monospace (ds-id) — use for EAN/ASIN/IDs. */
  mono?: boolean;
  /**
   * Column width for the shared grid template. number => px, string => any
   * grid track value ('1fr', 'minmax(120px,2fr)', '20%'). Omit => '1fr'.
   */
  width?: string | number;
  /** Allow click-to-sort on this column's header. */
  sortable?: boolean;
  /** Custom cell renderer. Falls back to row[key] when omitted. */
  render?: (row: Row, index: number) => React.ReactNode;
  /**
   * Value used for sorting (numbers sort numerically, strings via locale
   * compare). Falls back to row[key] when omitted.
   */
  sortValue?: (row: Row) => string | number;
}

export interface DsDataGridProps<Row> {
  columns: DsColumn<Row>[];
  rows: Row[];
  /** Stable row identity. Defaults to the array index. */
  getRowKey?: (row: Row, index: number) => string | number;
  onRowClick?: (row: Row, index: number) => void;
  /** Highlights the row whose getRowKey matches (ds-tr-selected). */
  selectedKey?: string | number;
  emptyText?: React.ReactNode;
  /** Sticky header (default true). */
  stickyHeader?: boolean;
  /** Max body height in px before the body scrolls (default 560). */
  maxHeight?: number;
  /** Row height in px (default 32, matching ds-tbl dense rows). */
  rowHeight?: number;
  /** Rendered inside a .ds-sticky-footer below the body (e.g. totals). */
  footer?: React.ReactNode;
  /** Dim the grid + show a loading hint over an empty body. */
  loading?: boolean;
  initialSort?: { key: string; dir: DsSortDir };
  className?: string;
}

/** Below this many rows we render directly without windowing. */
const VIRTUAL_THRESHOLD = 50;
const DEFAULT_ROW_HEIGHT = 32;
const DEFAULT_MAX_HEIGHT = 560;

function alignClass(align?: DsGridAlign): string {
  return align === 'right' ? 'ds-r' : align === 'center' ? 'ds-c' : '';
}

function trackOf<Row>(col: DsColumn<Row>): string {
  if (col.width == null) return 'minmax(0, 1fr)';
  return typeof col.width === 'number' ? `${col.width}px` : col.width;
}

function rawValue<Row>(col: DsColumn<Row>, row: Row): unknown {
  if (col.sortValue) return col.sortValue(row);
  return (row as Record<string, unknown>)[col.key];
}

function cellContent<Row>(col: DsColumn<Row>, row: Row, index: number): React.ReactNode {
  if (col.render) return col.render(row, index);
  const v = (row as Record<string, unknown>)[col.key];
  return v == null ? null : (v as React.ReactNode);
}

function compareValues(a: unknown, b: unknown): number {
  const an = a == null;
  const bn = b == null;
  if (an && bn) return 0;
  if (an) return -1;
  if (bn) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const as = typeof a === 'number' ? a : String(a);
  const bs = typeof b === 'number' ? b : String(b);
  if (typeof as === 'number' && typeof bs === 'number') return as - bs;
  return String(as).localeCompare(String(bs), undefined, { numeric: true, sensitivity: 'base' });
}

interface RowData<Row> {
  rows: Row[];
  columns: DsColumn<Row>[];
  gridTemplate: string;
  getRowKey?: (row: Row, index: number) => string | number;
  onRowClick?: (row: Row, index: number) => void;
  selectedKey?: string | number;
  clickable: boolean;
}

function gridCells<Row>(columns: DsColumn<Row>[], row: Row, index: number): React.ReactNode {
  return columns.map((col) => {
    const cls = ['ds-grid-cell', alignClass(col.align), col.mono ? 'ds-id' : '']
      .filter(Boolean)
      .join(' ');
    return (
      <div key={col.key} className={cls}>
        {cellContent(col, row, index)}
      </div>
    );
  });
}

/** Windowed row renderer for react-window's List. */
function VirtualRow<Row>({
  index,
  style,
  rows,
  columns,
  gridTemplate,
  getRowKey,
  onRowClick,
  selectedKey,
  clickable,
}: RowComponentProps<RowData<Row>>) {
  const row = rows[index];
  if (row === undefined) return null;
  const key = getRowKey ? getRowKey(row, index) : index;
  const selected = selectedKey != null && key === selectedKey;
  const cls = ['ds-grid-row', selected ? 'ds-tr-selected' : '', clickable ? 'ds-grid-clickable' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={cls}
      role="row"
      style={{ ...style, gridTemplateColumns: gridTemplate }}
      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
    >
      {gridCells(columns, row, index)}
    </div>
  );
}

export function DsDataGrid<Row>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  selectedKey,
  emptyText = 'No data',
  stickyHeader = true,
  maxHeight = DEFAULT_MAX_HEIGHT,
  rowHeight = DEFAULT_ROW_HEIGHT,
  footer,
  loading = false,
  initialSort,
  className = '',
}: DsDataGridProps<Row>) {
  const [sort, setSort] = useState<{ key: string; dir: DsSortDir } | null>(initialSort ?? null);

  const gridTemplate = useMemo(() => columns.map(trackOf).join(' '), [columns]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    // Decorate-sort-undecorate keeps original index stable for ties.
    return rows
      .map((row, i) => ({ row, i, v: rawValue(col, row) }))
      .sort((a, b) => {
        const c = compareValues(a.v, b.v);
        return c !== 0 ? c * factor : a.i - b.i;
      })
      .map((d) => d.row);
  }, [rows, columns, sort]);

  const toggleSort = (col: DsColumn<Row>) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: 'asc' };
      if (prev.dir === 'asc') return { key: col.key, dir: 'desc' };
      return null; // third click clears the sort
    });
  };

  const clickable = !!onRowClick;
  const isEmpty = sortedRows.length === 0;

  const header = (
    <div
      className={['ds-grid-head', stickyHeader ? 'ds-grid-head-sticky' : ''].filter(Boolean).join(' ')}
      role="row"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {columns.map((col) => {
        const active = sort?.key === col.key;
        const caret = active ? (sort?.dir === 'asc' ? '▲' : '▼') : '';
        const cls = ['ds-grid-th', alignClass(col.align), col.sortable ? 'ds-grid-th-sortable' : '']
          .filter(Boolean)
          .join(' ');
        return (
          <div
            key={col.key}
            role="columnheader"
            className={cls}
            aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
            onClick={col.sortable ? () => toggleSort(col) : undefined}
          >
            <span className="ds-grid-th-label">{col.header}</span>
            {caret ? <span className="ds-grid-caret">{caret}</span> : null}
          </div>
        );
      })}
    </div>
  );

  // Body height: cap at maxHeight, never taller than the rows need.
  const bodyHeight = Math.min(sortedRows.length * rowHeight, maxHeight);
  const virtualize = sortedRows.length > VIRTUAL_THRESHOLD;

  const rowData: RowData<Row> = {
    rows: sortedRows,
    columns,
    gridTemplate,
    getRowKey,
    onRowClick,
    selectedKey,
    clickable,
  };

  let body: React.ReactNode;
  if (isEmpty) {
    body = (
      <div className="ds-grid-empty">
        {loading ? (
          <div className="ds-grid-loading-text">Loading…</div>
        ) : (
          <EmptyState title={emptyText} />
        )}
      </div>
    );
  } else if (virtualize) {
    body = (
      <List<RowData<Row>>
        className="ds-grid-vlist"
        rowComponent={VirtualRow}
        rowCount={sortedRows.length}
        rowHeight={rowHeight}
        rowProps={rowData}
        style={{ height: bodyHeight }}
      />
    );
  } else {
    body = (
      <div className="ds-grid-body" style={{ maxHeight }}>
        {sortedRows.map((row, index) => {
          const key = getRowKey ? getRowKey(row, index) : index;
          const selected = selectedKey != null && key === selectedKey;
          const cls = ['ds-grid-row', selected ? 'ds-tr-selected' : '', clickable ? 'ds-grid-clickable' : '']
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={key}
              role="row"
              className={cls}
              style={{ height: rowHeight, gridTemplateColumns: gridTemplate }}
              onClick={onRowClick ? () => onRowClick(row, index) : undefined}
            >
              {gridCells(columns, row, index)}
            </div>
          );
        })}
      </div>
    );
  }

  const rootCls = ['ds-grid', loading ? 'ds-grid-is-loading' : '', className].filter(Boolean).join(' ');

  return (
    <div className={rootCls} role="table">
      <div className="ds-grid-scroll">
        {header}
        {body}
      </div>
      {footer != null ? <div className="ds-sticky-footer ds-grid-footer">{footer}</div> : null}
    </div>
  );
}

export default DsDataGrid;
